import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { toast } from "sonner";
import { Send, CheckCircle2, ArrowRight } from "lucide-react";

const INK = "#0F3D3A";
const TEAL = "#117A7A";
const GOLD = "#D4A84C";
const PLANS = ["Lestari Growth", "Lestari Enterprise", "Not sure yet"];

const field = "w-full rounded-md border border-[#0F3D3A]/20 bg-white px-3 py-2 text-sm text-[#0F3D3A] placeholder:text-[#0F3D3A]/40 focus:outline-none focus:ring-2 focus:ring-[#117A7A]/40 focus:border-[#117A7A]";

export function SubscribeLeadForm({ open, onOpenChange, defaultPlan }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", uen: "", plan_of_interest: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) { setDone(false); setForm((f) => ({ ...f, plan_of_interest: defaultPlan || "" })); }
  }, [open, defaultPlan]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads/subscribe", {
        name: form.name.trim(), email: form.email.trim(), company: form.company.trim(),
        uen: form.uen.trim() || null, plan_of_interest: form.plan_of_interest || null,
        message: form.message.trim() || null,
      });
      setDone(true);
    } catch (err) { toast.error(apiErr(err, "Could not submit your enquiry")); }
    finally { setBusy(false); }
  };

  const valid = form.name.trim().length >= 2 && form.email.includes("@") && form.company.trim().length >= 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-[#0F3D3A] border-[#0F3D3A]/10 max-w-md" data-testid="subscribe-dialog">
        {done ? (
          <div className="text-center py-4" data-testid="subscribe-success">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-3" style={{ color: TEAL }} />
            <h3 className="font-bold text-xl" style={{ fontFamily: "Georgia, serif" }}>Thank you — we'll be in touch</h3>
            <p className="text-sm text-[#0F3D3A]/65 mt-2">
              Your enquiry has reached the SusTek sales team. Expect a reply within one business day to help you get
              started on {form.plan_of_interest || "the right plan"}.
            </p>
            <button onClick={() => onOpenChange(false)} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full text-white" style={{ background: INK }} data-testid="subscribe-close-btn">
              Done
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-[#0F3D3A]">Talk to sales about subscribing</DialogTitle>
              <DialogDescription className="text-[#0F3D3A]/60">
                {defaultPlan ? `Interested in ${defaultPlan}? ` : ""}Tell us a little about your organisation and our team
                will help you subscribe.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-1" data-testid="subscribe-form">
              <div>
                <label className="text-xs font-medium text-[#0F3D3A]/70">Full name *</label>
                <input className={field} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Amanda Lee" data-testid="subscribe-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#0F3D3A]/70">Work email *</label>
                  <input type="email" className={field} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com.sg" data-testid="subscribe-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0F3D3A]/70">Company *</label>
                  <input className={field} value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Company Pte Ltd" data-testid="subscribe-company" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#0F3D3A]/70">UEN / Reg. no.</label>
                  <input className={field} value={form.uen} onChange={(e) => set("uen", e.target.value.toUpperCase())} placeholder="201812345K" data-testid="subscribe-uen" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0F3D3A]/70">Plan of interest</label>
                  <select className={field} value={form.plan_of_interest} onChange={(e) => set("plan_of_interest", e.target.value)} data-testid="subscribe-plan">
                    <option value="">Select…</option>
                    {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#0F3D3A]/70">Message (optional)</label>
                <textarea className={`${field} min-h-[70px] resize-none`} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="What would you like to achieve with Lestari?" data-testid="subscribe-message" />
              </div>
              <button type="submit" disabled={busy || !valid} data-testid="subscribe-submit"
                className="w-full inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-full text-white transition-colors disabled:opacity-50"
                style={{ background: valid ? INK : "#0F3D3A99" }}>
                <Send className="h-4 w-4" /> {busy ? "Sending…" : "Submit enquiry"} <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-[11px] text-center text-[#0F3D3A]/45">Your details are shared only with the SusTek sales team.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
